<script>

import * as turf from '@turf/turf'
import mapboxgl from 'mapbox-gl';

// Import plugins
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

// Import styles
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import 'mapbox-gl/dist/mapbox-gl.css'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'

export default {
  name: 'MapBox',

  props: {
    source: {
      required: false,
      type: Object,
      default: function () {
        return null;
      },
    },
    draw: {
      required: true,
      type: Boolean,
    },
    token: {
      required: true,
      type: String,
    },
    render: {
      required: false,
      default: true,
      type: Boolean,
    },
    popups: {
      required: false,
      type: Boolean,
      default: false,
    },
    mapId: {
      required: true,
      type: String,
      default: ''
    }
  },

  data() {
    return {
      // mapStyle: 'mapbox://styles/mapbox/satellite-streets-v11',
      mapStyle: 'mapbox://styles/mapbox/outdoors-v11',
      map: null,
      geography: null,
      mapDraw: {},
      loaded: false,
      center: [-93.627594, 42.025494],
      identifier: 'map-' + Math.floor(10000 * Math.random()),
      fullBounds: new mapboxgl.LngLatBounds(),
      sources: [],
      loading: true,
    };
  },

  watch: {
    source(newVal) {
      this.processGeography(newVal)
      if (this.map === null) {
        this.renderMap()
      } else {
        if (this.loaded) {
          this.clearMap()
          this.postMapRender()
        }
      }
    },
  },
  created() {
    this.processGeography(this.source)
  },
  mounted() {
    this.renderMap()
  },

  methods: {
    /**
     * Initiates creating the map and loading the data.
     */
    renderMap: function () {
      this.createMap();
      this.addControls();

      this.map.on('load', function () {
        this.loaded = true
        this.map.resize()
        this.postMapRender()
      }.bind(this));

      this.map.once('idle', function() {
        this.map.resize()
      }.bind(this));
    },

    /**
     * Creates map with added plugins.
     */
    createMap() {
      mapboxgl.accessToken = this.token;

      this.map = new mapboxgl.Map({
        container: this.mapId,
        style: this.mapStyle, // stylesheet location
        center: this.center,
        maxZoom: 7,
        zoom: 5, // starting zoom
      });

      if (this.draw) {
        this.mapDraw = new MapboxDraw({
          displayControlsDefault: false,
          controls: {
            polygon: true,
            combine_features: true,
            uncombine_features: true,
            trash: true,
          }
        })
        this.map.on('draw.create', (e) => {
          this.broadcastUpdate()
        });
        this.map.on('draw.update', (e) => {
          this.broadcastUpdate()
        });
        this.map.on('draw.delete', (e) => {
          this.broadcastUpdate()
        });
      }
    },

    /**
     * Adds data to map and sets bounds.
     */
    postMapRender() {
      if (this.geography != null) {
        this.addGeography(this.geography);
        if (this.geography.features.length > 1) {
          this.setMapBoundsToFit(this.geography);
        }
      }
      this.map.resize();
    },

    /**
     * Adds map controls
     */
    addControls() {
      if (this.draw) {
        this.map.addControl(new MapboxGeocoder({
          accessToken: mapboxgl.accessToken,
          mapboxgl: mapboxgl
        }), 'top-right');
      }

      // Add zoom and rotation controls to the map.
      this.map.addControl(new mapboxgl.NavigationControl());

      if (this.draw) {
        this.map.addControl(this.mapDraw, 'top-left');
      }
    },

    /**
     * Reads an inputted geography
     */
    processGeography(geography) {

      if (geography && geography.type !== 'FeatureCollection') {
        console.warn('Source is not a feature collection.')
        geography = null
      }

      if (geography && geography.features.length > 0) {
        this.geography = turf.featureCollection(geography.features)
        this.center = this.calculateCenter(this.geography)
      } else {
        this.geography = null
        this.center = [-93.627594, 42.025494]
      }
    },

    /**
     * Returns geography as a feature collection. (For external use)
     */
    getGeography() {
      let data = turf.featureCollection(this.mapDraw.getAll().features);

      if (data.features.length > 0) {
        this.geography = data
      } else {
        this.geography = null
      }

      return {
        'geography': this.geography,
      };
    },

    /**
     * Computes the center of a geometry
     */
    calculateCenter(geometry) {
      return turf.getGeom(turf.center(geometry)).coordinates
    },

    /**
     * Add geography layer on the map.
     */
    addGeography(geography) {

      let newSourceId = Math.random().toString(36).substring(2, 15);
      this.sources.push(newSourceId);

      this.map.addSource(newSourceId, {
        'type': 'geojson',
        'data': geography,
      });

      this.map.addLayer({
            'id': newSourceId,
            'type': 'heatmap',
            'source': newSourceId,
            'maxzoom': 11,
            'paint': {
              'heatmap-weight': [
                'interpolate',
                ['linear'],
                ['get', 'amount'], 0, 0, 6, 1
              ],
              'heatmap-intensity': [
                'interpolate',
                ['linear'],
                ['zoom'], 0, 1, 9, 3
              ],
              'heatmap-color': [
                'interpolate',
                ['linear'],
                ['heatmap-density'],
                0,
                'rgba(33,102,172,0)',
                .1,
                '#FFFF00',
                .2,
                '#FFE300',
                .3,
                '#FEC600',
                .4,
                '#FF9E00',
                .5,
                '#FE8D00',
                .6,
                '#FF7202',
                .7,
                '#FF3901',
                .8,
                '#FF1C02',
                .9,
                '#FE0000',
                1,
                '#d90000',
              ],
              'heatmap-radius': [
                'interpolate',
                ['linear'],
                ['zoom'], 0, 2, 9, 20
              ],
              'heatmap-opacity': [
                'interpolate',
                ['linear'],
                ['zoom'], 7, 1, 9, 0
              ]
            }
          },
          'waterway-label')
    },

    /**
     * Remove all geography layers on the map.
     */
    clearMap() {
      if (this.draw) {
        this.mapDraw.deleteAll()
      } else {
        this.sources.forEach((source) => {
          if (this.map.getSource(source) && this.map.isSourceLoaded(source)) {
            this.map.removeLayer(source)
            this.map.removeSource(source)
          }
        })
      }
    },

    /**
     * Sets the bounds of a map to fit the given geography.
     */
    setMapBoundsToFit(geography) {
      this.fullBounds = new mapboxgl.LngLatBounds();
      let bbox = turf.bbox(geography);

      this.fullBounds.extend([bbox[0], bbox[1]]);
      this.fullBounds.extend([bbox[2], bbox[3]]);

      this.map.fitBounds(this.fullBounds, {
        padding: 50,
        duration: 0,
      });
    },

    /**
     * broadcasts the current data.
     */
    broadcastUpdate() {
      this.$emit('updateGeography', {
        'geography': this.geography,
        'type': 'mapbox',
      });
    },
  },
};

</script>

<template>
  <div class="w-full text-black">
    <div :id="mapId" class="h-128 w-full rounded-lg">
      <div v-if="this.loading" style="background: rgba(250, 250, 250, 0.66)" class="absolute inline z-50 m-2 text-xl rounded p-1 border">Loading...</div>
    </div>
  </div>
</template>
